import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, CreditCard, Copy, CheckCheck, Check, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { useUser } from '@/contexts/UserContext';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function ChatPage() {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const user = useUser();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [wsStatus, setWsStatus] = useState('connecting');
  const [offer, setOffer] = useState(null);
  const [typingUser, setTypingUser] = useState(null);
  const [readMsgIds, setReadMsgIds] = useState(new Set());
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const lastTypingSentRef = useRef(0);

  useEffect(() => {
    api.get(`/offers/${offerId}`).then(r => setOffer(r.data)).catch(() => {});
  }, [offerId]);

  useEffect(() => {
    if (!user?.user_id) return;
    api.get(`/messages/${offerId}`).then(r => {
      setMessages(r.data);
      // Initialize read state from DB: messages I sent that have been read by the other party
      const readIds = new Set();
      r.data.forEach(m => {
        if (m.sender_id === user.user_id && m.read_by && m.read_by.length > 0) {
          readIds.add(m.message_id);
        }
      });
      if (readIds.size > 0) setReadMsgIds(readIds);
    }).catch(() => {});
  }, [offerId, user?.user_id]);

  const connectWs = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;
    const backendUrl = window.location.origin;
    const wsUrl = backendUrl.replace('https://', 'wss://').replace('http://', 'ws://');
    try {
      const ws = new WebSocket(`${wsUrl}/api/ws/chat/${offerId}`);
      ws.onopen = () => {
        setWsStatus('connected');
        ws.send(JSON.stringify({ type: 'read' }));
      };
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'typing' && data.sender_id !== user?.user_id) {
            setTypingUser(data.sender_name);
            if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
            typingTimeoutRef.current = setTimeout(() => setTypingUser(null), 3000);
            return;
          }
          if (data.type === 'read' && data.reader_id !== user?.user_id) {
            const ids = data.message_ids || [];
            if (ids.length > 0) {
              setReadMsgIds(prev => {
                const next = new Set(prev);
                ids.forEach(id => next.add(id));
                return next;
              });
            }
            return;
          }
          if (data.type === 'message' || data.message_id) {
            setMessages(prev => {
              if (prev.some(m => m.message_id === data.message_id)) return prev;
              return [...prev, data];
            });
            if (data.sender_id !== user?.user_id && ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: 'read' }));
            }
          }
        } catch {}
      };
      ws.onclose = () => { setWsStatus('disconnected'); reconnectRef.current = setTimeout(connectWs, 3000); };
      ws.onerror = () => setWsStatus('disconnected');
      wsRef.current = ws;
    } catch {
      setWsStatus('disconnected');
      reconnectRef.current = setTimeout(connectWs, 5000);
    }
  }, [offerId, user?.user_id]);

  useEffect(() => {
    connectWs();
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, [connectWs]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, typingUser]);

  const sendTyping = () => {
    const now = Date.now();
    if (now - lastTypingSentRef.current < 2000) return;
    lastTypingSentRef.current = now;
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'typing' }));
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const msgText = text.trim();
    setText('');
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ text: msgText }));
    } else {
      try {
        const res = await api.post(`/messages/${offerId}`, { text: msgText });
        setMessages(prev => prev.some(m => m.message_id === res.data.message_id) ? prev : [...prev, res.data]);
      } catch {}
    }
  };

  const sendPaymentInfo = async () => {
    try {
      const res = await api.get('/users/payment-info');
      const info = res.data;
      if (!info.spin_number && !info.bank_name && !info.clabe) {
        toast.error('Primero agrega tus datos de cobro en tu Perfil');
        return;
      }
      let payMsg = '--- DATOS DE PAGO ---\n';
      if (info.spin_number) payMsg += `SPIN: ${info.spin_number}\n`;
      if (info.bank_name) payMsg += `Banco: ${info.bank_name}\n`;
      if (info.clabe) payMsg += `CLABE: ${info.clabe}\n`;
      payMsg += '---';
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ text: payMsg }));
      } else {
        await api.post(`/messages/${offerId}`, { text: payMsg });
      }
      toast.success('Datos de pago enviados');
    } catch { toast.error('Error al obtener datos de pago'); }
  };

  const copyText = (val) => navigator.clipboard.writeText(val).then(() => toast.success('Copiado!')).catch(() => {});

  const isRejected = offer?.status === 'rejected';
  const isAccepted = offer?.status === 'accepted';
  const isCompleted = offer?.status === 'completed';
  const isSeller = user && offer && user.user_id === offer.seller_id;
  const otherName = isSeller ? offer?.buyer_name : offer?.seller_name;
  const otherPic = isSeller ? offer?.buyer_picture : offer?.seller_picture;

  const isPaymentMsg = (t) => t?.includes('--- DATOS DE PAGO ---');

  // Group consecutive messages by same sender for cleaner UI
  const shouldShowAvatar = (idx) => {
    if (idx === 0) return true;
    return messages[idx].sender_id !== messages[idx - 1].sender_id;
  };

  const isLastInGroup = (idx) => {
    if (idx === messages.length - 1) return true;
    return messages[idx].sender_id !== messages[idx + 1].sender_id;
  };

  return (
    <div data-testid="chat-page" className="flex flex-col bg-zinc-950" style={{ height: '100dvh' }}>
      {/* Header */}
      <div className="shrink-0 bg-zinc-900 px-3 flex items-center gap-3" style={{ paddingTop:'max(env(safe-area-inset-top,0px),10px)', paddingBottom:'10px' }}>
        <button data-testid="chat-back-btn" onClick={() => navigate('/deals')} className="min-w-[40px] min-h-[40px] flex items-center justify-center">
          <ArrowLeft className="w-5 h-5 text-zinc-300" />
        </button>
        <Avatar className="w-10 h-10 border-2 border-zinc-700">
          <AvatarImage src={otherPic} />
          <AvatarFallback className="bg-zinc-800 text-zinc-300 text-sm font-body">{otherName?.charAt(0) || '?'}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="font-body font-bold text-zinc-100 text-sm truncate">{otherName || 'Chat'}</h1>
            <div data-testid="ws-status" className={`w-2 h-2 rounded-full shrink-0 ${wsStatus === 'connected' ? 'bg-emerald-400' : wsStatus === 'connecting' ? 'bg-amber-400 animate-pulse' : 'bg-red-400'}`} />
          </div>
          <p className="text-zinc-500 text-[11px] font-body truncate">
            {typingUser ? 'escribiendo...' : offer?.target_item_title || ''}
          </p>
        </div>
        {(isAccepted || isCompleted) && (
          <div className="bg-emerald-500/15 px-2.5 py-1 rounded-full">
            <span className="text-emerald-400 text-[10px] font-body font-bold">Cerrado</span>
          </div>
        )}
      </div>

      {/* Deal banner */}
      {(isAccepted || isCompleted) && (
        <div data-testid="chat-deal-banner" className="shrink-0 bg-emerald-500/10 px-4 py-2 text-center border-b border-emerald-500/20">
          <p className="font-body text-xs text-emerald-400">Trato cerrado — coordina la entrega por aqui</p>
        </div>
      )}

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-3 py-4 scrollbar-hide" style={{ backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(255,215,0,0.02) 0%, transparent 70%)' }}>
        {messages.length === 0 && (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-full bg-zinc-800/50 flex items-center justify-center mx-auto mb-4">
              <Phone className="w-7 h-7 text-zinc-600" />
            </div>
            <p className="text-zinc-500 font-body text-sm">Inicia la conversacion</p>
            <p className="text-zinc-600 font-body text-xs mt-1">Los mensajes son privados entre ambas partes</p>
          </div>
        )}

        <div className="space-y-0.5">
          {messages.map((msg, idx) => {
            const isMe = msg.sender_id === user?.user_id;
            const isPay = isPaymentMsg(msg.text);
            const showAvatar = shouldShowAvatar(idx);
            const lastInGroup = isLastInGroup(idx);

            return (
              <div key={msg.message_id} data-testid={`chat-msg-${msg.message_id}`}
                className={`flex items-end gap-1.5 ${isMe ? 'flex-row-reverse' : ''} ${showAvatar ? 'mt-3' : 'mt-0.5'}`}>
                {/* Avatar spacer / avatar */}
                {!isMe && (
                  <div className="w-7 shrink-0">
                    {lastInGroup && (
                      <Avatar className="w-7 h-7">
                        <AvatarImage src={msg.sender_picture} />
                        <AvatarFallback className="bg-zinc-800 text-zinc-400 text-[10px]">{msg.sender_name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                    )}
                  </div>
                )}

                {/* Bubble */}
                <div className={`max-w-[78%] relative ${
                  isPay
                    ? 'bg-emerald-900/40 border border-emerald-700/40 rounded-2xl px-3.5 py-2.5'
                    : isMe
                      ? `bg-primary rounded-2xl ${lastInGroup ? 'rounded-br-md' : 'rounded-br-md'} px-3.5 py-2`
                      : `bg-zinc-800 rounded-2xl ${lastInGroup ? 'rounded-bl-md' : 'rounded-bl-md'} px-3.5 py-2`
                }`}>
                  {/* Sender name for first in group (not me) */}
                  {!isMe && showAvatar && (
                    <p className="text-primary text-[11px] font-body font-bold mb-0.5">{msg.sender_name}</p>
                  )}

                  {isPay ? (
                    <div className="space-y-2">
                      <p className="text-emerald-400 text-[11px] font-body font-bold uppercase tracking-wider">Datos de Pago</p>
                      {msg.text.split('\n').filter(l => l && l !== '---' && !l.includes('DATOS DE PAGO')).map((line, i) => {
                        const [label, ...rest] = line.split(':');
                        const value = rest.join(':').trim();
                        return (
                          <div key={i} className="flex items-center justify-between gap-3">
                            <div>
                              <span className="text-zinc-400 text-[11px] font-body">{label}</span>
                              <span className="text-zinc-100 text-sm font-body font-bold block">{value}</span>
                            </div>
                            {value && (
                              <button onClick={() => copyText(value)} className="p-1.5 bg-zinc-800/60 rounded-lg hover:bg-zinc-700/60 transition-colors">
                                <Copy className="w-3.5 h-3.5 text-zinc-400" />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className={`font-body text-[15px] leading-relaxed break-words whitespace-pre-wrap ${isMe ? 'text-primary-foreground' : 'text-zinc-100'}`}>
                      {msg.text}
                    </p>
                  )}

                  {/* Timestamp + read receipt */}
                  <div className={`flex items-center gap-1 mt-1 ${isMe ? 'justify-end' : ''}`}>
                    <span className={`text-[10px] ${isPay ? 'text-emerald-500/60' : isMe ? 'text-primary-foreground/50' : 'text-zinc-500'}`}>
                      {new Date(msg.created_at).toLocaleTimeString('es-MX', { hour:'2-digit', minute:'2-digit' })}
                    </span>
                    {isMe && (
                      readMsgIds.has(msg.message_id)
                        ? <CheckCheck data-testid="read-receipt-read" className="w-3.5 h-3.5 text-blue-400" />
                        : <Check data-testid="read-receipt-sent" className="w-3.5 h-3.5 text-primary-foreground/40" />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Typing indicator */}
        {typingUser && (
          <div data-testid="typing-indicator" className="flex items-end gap-1.5 mt-3">
            <Avatar className="w-7 h-7">
              <AvatarImage src={otherPic} />
              <AvatarFallback className="bg-zinc-800 text-zinc-400 text-[10px]">{otherName?.charAt(0)}</AvatarFallback>
            </Avatar>
            <div className="bg-zinc-800 rounded-2xl rounded-bl-md px-4 py-3">
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-zinc-500 animate-bounce" style={{ animationDelay: '0ms', animationDuration: '0.8s' }} />
                <span className="w-2 h-2 rounded-full bg-zinc-500 animate-bounce" style={{ animationDelay: '200ms', animationDuration: '0.8s' }} />
                <span className="w-2 h-2 rounded-full bg-zinc-500 animate-bounce" style={{ animationDelay: '400ms', animationDuration: '0.8s' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      {isRejected ? (
        <div className="shrink-0 bg-zinc-900 p-4 text-center" style={{ paddingBottom:'max(env(safe-area-inset-bottom,0px),12px)' }}>
          <p className="text-red-400 text-sm font-body">Chat deshabilitado — oferta rechazada</p>
        </div>
      ) : (
        <div className="shrink-0 bg-zinc-900" style={{ paddingBottom:'max(env(safe-area-inset-bottom,0px),8px)' }}>
          {isSeller && (isAccepted || isCompleted) && (
            <div className="px-3 pt-2">
              <button data-testid="chat-payment-btn" onClick={sendPaymentInfo}
                className="w-full flex items-center justify-center gap-2 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 font-body text-xs font-bold hover:bg-emerald-500/20 transition-colors">
                <CreditCard className="w-3.5 h-3.5" /> Enviar Datos de Pago
              </button>
            </div>
          )}
          <form onSubmit={sendMessage} className="p-2 flex items-end gap-2">
            <div className="flex-1 relative">
              <input data-testid="chat-input" value={text}
                onChange={e => { setText(e.target.value); sendTyping(); }}
                placeholder="Mensaje..."
                className="w-full rounded-full bg-zinc-800 border border-zinc-700 text-zinc-100 font-body text-sm pl-4 pr-4 py-3 focus:outline-none focus:border-zinc-500 placeholder:text-zinc-500 transition-colors"
                enterKeyHint="send" />
            </div>
            <button data-testid="chat-send-btn" type="submit" disabled={!text.trim()}
              className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-all ${text.trim() ? 'bg-primary text-primary-foreground scale-100' : 'bg-zinc-800 text-zinc-600 scale-95'}`}>
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
